import { useEffect, useState, type ReactNode } from 'react'
import { Loader, Popover } from '@systemeio/components'
import type { Editor, Range } from 'slate'
import { ReactEditor } from 'slate-react'
import { ListBox } from 'shared/components/list-box'
import type { Suggestion } from './types'

type AnchorElement = {
  getBoundingClientRect: () => DOMRect
}

interface MentionSuggestionsProps {
  target: Range | null
  editor: Editor
  items: Suggestion[]
  onSelect: (item: Suggestion) => void
  isLoading: boolean
}

const transformMentionsToSuggestions = (
  mentions: Suggestion[],
): {
  value: string
  label: ReactNode
}[] =>
  mentions.map(mention => ({
    value: mention.userId.toString(),
    label: (
      <div
        className="flex min-h-8 min-w-0 items-center justify-start gap-2 px-4"
        title={mention.displayName}
      >
        {mention.profileImageUrl && (
          <img
            className="size-5 shrink-0 rounded-full"
            width={20}
            height={20}
            src={mention.profileImageUrl}
            alt={`Avatar of ${mention.displayName}`}
          />
        )}
        <strong className="line-clamp-2 min-w-0 flex-1 text-[15px] wrap-break-word">
          {mention.displayName}
        </strong>
      </div>
    ),
  }))

export const MentionSuggestions = ({
  target,
  editor,
  items,
  onSelect,
  isLoading,
}: MentionSuggestionsProps): ReactNode | null => {
  const [listItems, setListItems] = useState<{ label: ReactNode; value: string }[]>([])
  const [anchorElement, setAnchorElement] = useState<AnchorElement | null>(null)

  useEffect(() => {
    if (target && items.length > 0) {
      const listItems = transformMentionsToSuggestions(items)
      setListItems(listItems)
    }
  }, [target, items])

  useEffect(() => {
    if (target) {
      const domRange = ReactEditor.toDOMRange(editor, target)
      setAnchorElement({ getBoundingClientRect: () => domRange.getBoundingClientRect() })
    } else {
      setAnchorElement(null)
    }
  }, [target, editor])

  if (!target) return null

  return (
    <Popover.Root open>
      <Popover.Portal>
        <Popover.Positioner anchor={anchorElement} side="top" align="start" sideOffset={20}>
          <Popover.Popup className="z-50">
            <div className="z-100 my-1.5 max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-hidden">
              {!isLoading || listItems.length > 0 ? (
                <ListBox
                  options={listItems}
                  onChange={userId => {
                    const suggestion = items.find(item => item.userId === Number(userId))
                    if (suggestion) {
                      onSelect(suggestion)
                    }
                  }}
                />
              ) : (
                <div className="flex items-center justify-center py-3.5">
                  <Loader />
                </div>
              )}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
